import {
  Links,
  LinksFunction,
  Meta,
  MetaFunction,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useRouteError,
} from 'react-router'
import type { LoaderFunction } from 'react-router'
import { AuthProvider } from '~/auth/AuthProvider'
import { AuthState } from '~/auth/AuthState'
import { getAuthStateFromSession } from '~/auth/session.server'
import { ErrorPage } from '~/components/ErrorPage'
import { PageLoadingProgress } from '~/components/PageLoadingProgress'
import { setupMobiscroll } from '~/config/mobiscroll'
import { EnvContext } from '~/config/useEnv'
import { VariablesClient } from '~/config/variables.client'
import { variables, VariablesServer } from '~/config/variables.server'
// the ?url is required for some reason when using vite
// https://remix.run/docs/en/main/styling/css
import stylesheet from '~/tailwind.css?url'
import {
  GoogleTagManagerNoscript,
  GoogleTagManagerScript,
} from '~/utils/googleAnalytics'
import { useNonce } from '~/utils/nonce-provider'

import { faviconLinks } from './faviconLinks'

setupMobiscroll()

export const meta: MetaFunction = () => [
  { charset: 'utf-8' },
  { title: 'mein.joviva' },
  { name: 'viewport', content: 'width=device-width,initial-scale=1' },
]

export const links: LinksFunction = () => [
  { rel: 'stylesheet', href: stylesheet },
  ...faviconLinks,
]

declare global {
  interface Window {
    ENV: VariablesClient | undefined
    dataLayer: unknown[] | undefined
    grecaptcha?: {
      enterprise?: {
        ready: (cb: () => void) => void
        execute: (siteKey: string, opts: { action: string }) => Promise<string>
      }
    }
  }
}

type LoaderReturnType = {
  ENV: Pick<
    VariablesServer,
    | 'ENVIRONMENT_NAME'
    | 'NODE_ENV'
    | 'SENTRY_DSN'
    | 'SENTRY_ENVIRONMENT'
    | 'SENTRY_RELEASE'
    | 'DEACTIVATED_FEATURES'
    | 'GOOGLE_TAG_MANAGER_ID'
    | 'STORYBLOK_PREVIEW_URL'
    | 'RECAPTCHA_SITE_KEY'
  >
  authState: AuthState
}
export const loader: LoaderFunction = async ({ request }) => {
  const authState = await getAuthStateFromSession(request)

  const responseData = {
    authState: authState,
    // expose SOME server variables to the client
    ENV: {
      ENVIRONMENT_NAME: variables?.ENVIRONMENT_NAME,
      NODE_ENV: variables.NODE_ENV,
      SENTRY_DSN: variables.SENTRY_DSN,
      SENTRY_ENVIRONMENT: variables.SENTRY_ENVIRONMENT,
      SENTRY_RELEASE: process.env.SENTRY_RELEASE,
      DEACTIVATED_FEATURES: variables.DEACTIVATED_FEATURES,
      GOOGLE_TAG_MANAGER_ID: variables.GOOGLE_TAG_MANAGER_ID,
      STORYBLOK_PREVIEW_URL: variables.STORYBLOK_PREVIEW_URL,
      RECAPTCHA_SITE_KEY: variables.RECAPTCHA_SITE_KEY,
    },
  } satisfies LoaderReturnType

  // If tokens were refreshed, include the Set-Cookie headers
  if (authState.headers) {
    return Response.json(responseData, {
      headers: authState.headers,
    })
  }

  return Response.json(responseData)
}

const Document = ({
  children,
  nonce,
  gtmId,
  captcha,
}: {
  children: React.ReactNode
  nonce: string
  gtmId: string | undefined
  captcha: string | undefined
}) => {
  return (
    <html lang={'de'}>
      <head>
        <Meta />
        <Links />
        {gtmId && <GoogleTagManagerScript gtmId={gtmId} nonce={nonce} />}
        {captcha && (
          <script
            src={`https://www.google.com/recaptcha/enterprise.js?render=${captcha}`}
            async
            defer
            nonce={nonce}
          />
        )}
      </head>
      <body className='text-default'>
        <PageLoadingProgress />
        {children}
        {/* Manages scroll position for client-side transitions */}
        {/* If you use a nonce-based content security policy for scripts, you must provide the `nonce` prop. Otherwise, omit the nonce prop as shown here. */}
        <ScrollRestoration nonce={nonce} />

        {/* Script tags go here */}
        {/* If you use a nonce-based content security policy for scripts, you must provide the `nonce` prop. Otherwise, omit the nonce prop as shown here. */}
        <Scripts nonce={nonce} />

        {gtmId && <GoogleTagManagerNoscript gtmId={gtmId} />}
      </body>
    </html>
  )
}

export const ErrorBoundary = () => {
  const error = useRouteError()
  const nonce = useNonce()

  console.error('Root ErrorBoundary caught route error ', error)

  return (
    <Document
      nonce={nonce}
      // the GA_TRACKING_ID is exposed by the root loader.
      // Therefore, we cannot ensure its presence in the ErrorBoundary
      gtmId={undefined}
      captcha={undefined}
    >
      <ErrorPage error={error} />
    </Document>
  )
}

export default function App() {
  const { ENV, authState } = useLoaderData<LoaderReturnType>()
  const nonce = useNonce()

  return (
    <Document
      nonce={nonce}
      gtmId={ENV.GOOGLE_TAG_MANAGER_ID}
      captcha={ENV.RECAPTCHA_SITE_KEY}
    >
      {/* Child routes render here */}
      <AuthProvider initialState={authState}>
        <EnvContext.Provider value={ENV}>
          <Outlet />
        </EnvContext.Provider>
      </AuthProvider>
      <script
        dangerouslySetInnerHTML={{
          __html: `window.ENV = ${JSON.stringify(ENV)}`,
        }}
        nonce={nonce}
      />
    </Document>
  )
}
